import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Flower2, Mail, Lock } from "lucide-react";

const BG_IMG = "https://images.pexels.com/photos/5772283/pexels-photo-5772283.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false && user !== null) {
      const from = location.state?.from || "/chapters";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/chapters");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)]" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={BG_IMG} alt="Meditation at sunset" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2D3B31]/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="shloka text-xl md:text-2xl">
            यो मां पश्यति सर्वत्र सर्वं च मयि पश्यति
          </div>
          <p className="mt-3 text-sm italic font-serif-display opacity-90">
            "One who sees Me everywhere, and sees all things in Me..." — Gītā 6.30
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-2 mb-8">
            <Flower2 size={32} color="#D96C4A" />
            <span className="font-serif-display text-2xl text-[#2D3B31]">Gītā Sabhā</span>
          </div>
          <h1 className="font-serif-display text-4xl font-medium text-[#2D3B31]">Welcome back</h1>
          <p className="mt-2 text-[#5C6B5F]">Sign in to continue your reflection.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Email</label>
              <div className="mt-2 relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C]" />
                <input
                  type="email"
                  className="input-organic pl-11"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Password</label>
              <div className="mt-2 relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C]" />
                <input
                  type="password"
                  className="input-organic pl-11"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#C05B3B] bg-[#F2D8CF]/50 border border-[#F2D8CF] rounded-lg px-4 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button type="submit" className="btn-accent w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Entering..." : "Sign in"}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#5C6B5F]">
            New to the sabhā?{" "}
            <Link to="/register" className="text-[#D96C4A] hover:underline" data-testid="login-to-register">
              Begin your journey
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
